/**
 * blog-images.ts — event payloads for the blog article AI-images flow
 * (idea 2026-07-19-blog-article-images-like-maira, T1).
 *
 * Structural sibling of blog-authoring.ts, but SYNCHRONOUS end-to-end inside
 * ONE worker handler (unlike blog.authoring.* which dispatches to dotclaude
 * via an async webhook round-trip): the reactor claims
 * `blog.images.requested`, runs the 2-stage enhance→generate flow in-process
 * (LlmRouter for the enhance call, ImageRouter for image gen — both already
 * block/poll internally), writes `blog_posts_i18n.body_html`, and emits the
 * terminal `blog.images.completed` / `blog.images.failed` itself. No
 * dispatched/intermediate event and no public webhook are needed.
 *
 *   admin emit_event('blog.images.requested', {...}, {correlation_id})
 *     → worker handler (blog-images.ts):
 *         1. enhance (one LLM call) → per-image prompts + alts
 *         2. generate (ImageRouter, sequential) → download → sharp webp
 *         3. upload (wandero bucket) → insert <img class="ai-gen"> into body_html
 *         4. sanitize + persist blog_posts_i18n.body_html
 *     → emits blog.images.completed (with images[] + cost_usd) or
 *       blog.images.failed, admin Realtime (subscribed by correlation_id)
 *       flips generating→done.
 */

import { z } from 'zod';

export const BLOG_IMAGES_EVENTS = {
  REQUESTED: 'blog.images.requested',
  COMPLETED: 'blog.images.completed',
  FAILED: 'blog.images.failed',
  PLAN_REQUESTED: 'blog.images.plan.requested',
  PLAN_COMPLETED: 'blog.images.plan.completed',
  PLAN_FAILED: 'blog.images.plan.failed',
} as const;

// ---------------------------------------------------------------------------
// Plan (AC v2, 2026-07-27) — prompts are APPROVED BEFORE anything is generated
// ---------------------------------------------------------------------------

/**
 * One planned image: what the enhance stage proposes, and — after the author
 * edits it in the panel — exactly what will be generated. The same shape
 * travels out on `blog.images.plan.completed` and back in on
 * `blog.images.requested.plan`, so the round-trip is lossless and the author
 * sees the literal prompt that will be sent to the image model.
 */
export const blogImagesPlanItemSchema = z.object({
  placement: z.enum(['hero', 'section']),
  /** Required for placement==='section': 0-based index into the article's <h2> list. */
  section_index: z.number().int().min(0).optional(),
  /** Detailed EN image-generation prompt (editable by the author before approval). */
  prompt: z.string().min(1).max(8_000),
  /** Alt text in the article's language (editable by the author). */
  alt: z.string(),
});
export type BlogImagesPlanItem = z.infer<typeof blogImagesPlanItemSchema>;

/** Ask the worker to run ONLY the enhance stage and report the proposed prompts. */
export const blogImagesPlanRequestedPayloadSchema = z.object({
  post_id: z.number().int().positive(),
  lang_code: z.string().min(2).max(10),
  correlation_id: z.string().min(1),
});
export type BlogImagesPlanRequestedPayload = z.infer<
  typeof blogImagesPlanRequestedPayloadSchema
>;

export const blogImagesPlanCompletedPayloadSchema = z.object({
  post_id: z.number().int().positive(),
  correlation_id: z.string().min(1),
  images: z.array(blogImagesPlanItemSchema),
  /**
   * The shared visual contour the enhance stage chose for this article — one
   * style sentence appended to every prompt so a batch reads as one coherent
   * set rather than three unrelated pictures (AC v2 item 6). Already folded
   * into each item's `prompt`; surfaced separately so the panel can show the
   * author WHY the images will look alike.
   */
  style_contour: z.string().optional(),
});
export type BlogImagesPlanCompletedPayload = z.infer<
  typeof blogImagesPlanCompletedPayloadSchema
>;

export const blogImagesPlanFailedPayloadSchema = z.object({
  post_id: z.number().int().positive(),
  correlation_id: z.string().min(1),
  error: z.string(),
});
export type BlogImagesPlanFailedPayload = z.infer<typeof blogImagesPlanFailedPayloadSchema>;

// ---------------------------------------------------------------------------
// blog.images.requested — admin → worker
// ---------------------------------------------------------------------------

export const blogImagesRequestedPayloadSchema = z.object({
  post_id: z.number().int().positive(),
  /** Locale of the blog_posts_i18n row to illustrate (must have body_html). */
  lang_code: z.string().min(2).max(10),
  /** Reactor correlation_id, echoed back so admin Realtime can match the run. */
  correlation_id: z.string().min(1),
  /**
   * When true: strip any existing `<img class="ai-gen">` nodes from
   * body_html before generating + inserting the new set. When false/absent,
   * new images are inserted alongside whatever is already there (first run).
   */
  regenerate: z.boolean().optional(),
  /**
   * AC v2 (2026-07-27): pre-approved prompts. When present, the handler
   * SKIPS the enhance stage entirely and generates exactly these prompts —
   * this is the path the admin panel uses after the author has reviewed and
   * edited `blog.images.plan.completed`. When absent, the handler enhances
   * first, exactly as before (the blog-autopilot saga still emits the
   * plan-less shape, so its behaviour is unchanged).
   */
  plan: z.array(blogImagesPlanItemSchema).optional(),
  /**
   * Explicit generation quality (AC v2 item 3 — no silent defaults). Absent
   * only on the legacy/autopilot path, where the handler's documented `low`
   * remains in force.
   */
  quality: z.enum(['low', 'high']).optional(),
});

export type BlogImagesRequestedPayload = z.infer<typeof blogImagesRequestedPayloadSchema>;

// ---------------------------------------------------------------------------
// blog.images.completed — worker → admin (Realtime)
// ---------------------------------------------------------------------------

export const blogImagePlacementSchema = z.enum(['hero', 'section']);
export type BlogImagePlacement = z.infer<typeof blogImagePlacementSchema>;

export const blogImagesCompletedImageSchema = z.object({
  url: z.string().min(1),
  alt: z.string(),
  placement: blogImagePlacementSchema,
});

export const blogImagesCompletedPayloadSchema = z.object({
  post_id: z.number().int().positive(),
  correlation_id: z.string().min(1),
  images: z.array(blogImagesCompletedImageSchema),
  cost_usd: z.number().nonnegative(),
  /**
   * Whether `cost_usd` is real billed money or a guess (idea
   * 2026-07-19-blog-article-images-like-maira). `'billed'` = every image's
   * cost came from the provider/usage/legacy tiers (real); `'estimate'` = at
   * least one image fell back to the caps range-midpoint guess; `'none'` =
   * no cost could be resolved at all. Optional for backwards-compat with any
   * consumer built against the pre-existing payload shape.
   */
  cost_source: z.enum(['billed', 'estimate', 'none']).optional(),
});

export type BlogImagesCompletedPayload = z.infer<typeof blogImagesCompletedPayloadSchema>;

// ---------------------------------------------------------------------------
// blog.images.failed — worker → admin (Realtime)
// ---------------------------------------------------------------------------

export const blogImagesFailedPayloadSchema = z.object({
  post_id: z.number().int().positive(),
  correlation_id: z.string().min(1),
  error: z.string(),
});

export type BlogImagesFailedPayload = z.infer<typeof blogImagesFailedPayloadSchema>;
